// @flow

import invariant from 'invariant';
import type ResourceFactory from 'app/common/lib/ResourceFactory/server';
import QueuedResourceCall, {
  QueuedKeepAliveRequest,
} from 'app/common/lib/ResourceFactory/QueuedResourceCall';
import type { BodyFields, StatsDArray, StatsDJSON } from 'node/resources/StatsLogResource';

type InitBrowserOptions = {
  useKeepAlive?: boolean,
  stickyKeepAliveFallback?: boolean,
};
type StatUnit = 'c' | 'ms';
type TagValue = string | number | boolean;
type Tags = { [key: string]: ?TagValue };
type QueuedResource =
  | QueuedResourceCall<StatsDArray | StatsDJSON>
  | QueuedKeepAliveRequest<StatsDArray | StatsDJSON>;

const FLUSH_TIMEOUT_MS = 5000;
let _queuedResource: null | QueuedResource = null;
const _queuedResources: Array<QueuedResource> = [];

let _nodeResourceFactory: ?ResourceFactory = null;
let _useKeepAlive: null | boolean = null; // null signals that we have no preference
let _stickyKeepAliveFallback: boolean = true;

function flush() {
  if (__SERVER__) {
    // Stats are not queued on the server, so flush is a no-op
    return;
  }
  if (_queuedResources.length) {
    _queuedResources.forEach((queue) => queue.flush());
  }
}

function _enqueueServer(stat) {
  if (_nodeResourceFactory) {
    // No need to queue stats on the server, since statsD will handle that under the hood
    const resource = _nodeResourceFactory.create('StatsLogResource', {
      source: 'server',
      stats: [stat],
    });
    resource.callCreate();
  }
}

function enqueue(bucket: string, value: number, type: StatUnit, sampleRate: number, tags: Tags) {
  invariant(
    sampleRate >= 0 && sampleRate <= 1,
    `sampleRate must be between 0 and 1. Found ${String(sampleRate)}`,
  );

  const stat =
    process.env.NODE_ENV !== 'production'
      ? { bucket, value, type, sampleRate, tags }
      : [bucket, value, type, sampleRate, tags];

  if (__SERVER__) {
    _enqueueServer(stat);
  } else {
    if (!_queuedResource) {
      if (_useKeepAlive) {
        _queuedResource = new QueuedKeepAliveRequest(
          '/resource/StatsLogResource/create/',
          'StatsLogResource',
          (queue): BodyFields => ({ source: 'browser', stats: queue }),
          FLUSH_TIMEOUT_MS,
        );
        _queuedResource.setStickyFallback(_stickyKeepAliveFallback);
      } else {
        _queuedResource = new QueuedResourceCall(
          'StatsLogResource',
          (queue): BodyFields => ({ source: 'browser', stats: queue, keepAlive: false }),
          FLUSH_TIMEOUT_MS,
        );
      }
      _queuedResources.push(_queuedResource);
    }
    _queuedResource.enqueue(stat);
  }
}

export function getSampleRate(sampleRate?: number) {
  if (process.env.NODE_ENV !== 'production') {
    return 1;
  } else if (sampleRate) {
    return sampleRate;
  } else {
    return 0.1;
  }
}

// See webapp/node/handlers/statsdLogging/handlerMapping.js for any product or log-specific
// server-side handling you might want to layer on top of this basic message passing.
export default {
  increment(bucket: string, sampleRate?: number = 0.1, tags?: Tags = {}) {
    enqueue(bucket, 1, 'c', sampleRate, tags);
  },

  count(bucket: string, value: number, sampleRate?: number = 0.1, tags?: Tags = {}) {
    enqueue(bucket, value, 'c', sampleRate, tags);
  },

  timing(bucket: string, value: number, sampleRate?: number = 0.1, tags?: Tags = {}) {
    enqueue(bucket, value, 'ms', sampleRate, tags);
  },

  incrementNow(bucket: string, sampleRate?: number = 0.1, tags?: Tags = {}) {
    enqueue(bucket, 1, 'c', sampleRate, tags);
    flush();
  },

  countNow(bucket: string, value: number, sampleRate?: number = 0.1, tags?: Tags = {}) {
    enqueue(bucket, value, 'c', sampleRate, tags);
    flush();
  },

  timingNow(bucket: string, value: number, sampleRate?: number = 0.1, tags?: Tags = {}) {
    enqueue(bucket, value, 'ms', sampleRate, tags);
    flush();
  },

  initServer(resourceFactory: ResourceFactory) {
    _nodeResourceFactory = resourceFactory;
  },

  initBrowser({ useKeepAlive, stickyKeepAliveFallback }: InitBrowserOptions) {
    if (typeof useKeepAlive === 'boolean') {
      _useKeepAlive = useKeepAlive;
      _queuedResource = null;
      if (typeof stickyKeepAliveFallback === 'boolean') {
        _stickyKeepAliveFallback = stickyKeepAliveFallback;
      }
    }
  },

  __test_only_get_queue() {
    return _queuedResource ? _queuedResource._queue : [];
  },

  __test_only_clear() {
    _queuedResource = null;
  },
};
